// @ts-check
import { createLifetime } from './lifetime.js';
/** @typedef {import('../game/types.js').Action} Action */
/** @typedef {'start'|'select'|'confirm'|'previous'|'next'} PadControl */
/** @typedef {{actions:Set<Action>,start:boolean,select:boolean,confirm:boolean,nav:-1|0|1}} PadState */

const STICK_DEAD_ZONE = 0.35;
const STICK_CROUCH = 0.6;
const TRIGGER_PRESS = 0.5;

/** W3C standard mapping, by button position rather than printed label. An 8BitDo SN30 Pro
 * reports this layout in X-input, Switch, and macOS modes: bottom (B) jumps, left (Y) sprints
 * like the SNES run button, right (A) and top (X) fire Pulse, and the shoulders sprint.
 * @type {ReadonlyArray<readonly [number, Action]>}
 */
export const PAD_ACTIONS = [
  [0, 'jump'], [2, 'sprint'], [4, 'sprint'], [5, 'sprint'], [1, 'pulse'], [3, 'pulse'], [6, 'pulse'], [7, 'pulse'],
  [13, 'down'], [14, 'left'], [15, 'right'],
];
export const PAD_SELECT = 8;
export const PAD_START = 9;

/** An empty reading: no pad, or every control released. @returns {PadState} */
export function idlePad() { return { actions: new Set(), start: false, select: false, confirm: false, nav: 0 }; }

/** Read one pad into gameplay actions and menu controls.
 * D-pad Up is menu navigation only, so a stick drifting up never jumps.
 * @param {Gamepad} pad @returns {PadState}
 */
export function readPad(pad) {
  const state = idlePad();
  /** @param {number} index */
  const on = (index) => { const button = pad.buttons[index]; return !!button && (button.pressed || button.value > TRIGGER_PRESS); };
  /** @param {number} index */
  const axis = (index) => { const value = pad.axes[index] ?? 0; return Math.abs(value) > STICK_DEAD_ZONE ? value : 0; };
  const x = axis(0), y = axis(1);
  if (x < 0) state.actions.add('left');
  if (x > 0) state.actions.add('right');
  if (y > STICK_CROUCH) state.actions.add('down');
  if (pad.mapping === 'standard') {
    for (const [index, action] of PAD_ACTIONS) if (on(index)) state.actions.add(action);
    state.start = on(PAD_START); state.select = on(PAD_SELECT); state.confirm = on(0) || on(1);
    if (on(12) || on(14)) state.nav = -1; else if (on(13) || on(15)) state.nav = 1;
  }
  if (!state.nav && (x || y)) state.nav = (Math.abs(y) >= Math.abs(x) ? y : x) < 0 ? -1 : 1;
  return state;
}

/** Merge every connected pad, so a second controller can take over without a handoff.
 * Returns null when no pad is connected.
 * @param {ArrayLike<Gamepad|null>} pads @returns {PadState|null}
 */
export function readPads(pads) {
  /** @type {PadState|null} */ let merged = null;
  for (const pad of Array.from(pads)) {
    if (!pad || !pad.connected) continue;
    const state = readPad(pad);
    merged ??= idlePad();
    for (const action of state.actions) merged.actions.add(action);
    merged.start ||= state.start; merged.select ||= state.select; merged.confirm ||= state.confirm;
    merged.nav ||= state.nav;
  }
  return merged;
}

/** Poll every connected pad once per display frame, from the first `gamepadconnected` (or a pad already present)
 * until none remains. Gamepads have no button events, so polling is the only way to read them.
 * `poll` gets the merged reading and the previous one, so callers detect presses as edges. It gets null once,
 * when the last pad disconnects, and polling stops until the next connection.
 * @param {EventTarget} target
 * @param {{gamepads:()=>ArrayLike<Gamepad|null>,request:(callback:FrameRequestCallback)=>number,cancel:(id:number)=>void}} devices
 * @param {(state:PadState|null, previous:PadState)=>void} poll
 */
export function watchPads(target, devices, poll) {
  const life = createLifetime();
  let frame = 0;
  let previous = idlePad();
  // A page can forbid gamepads: askqp.com sends Permissions-Policy gamepad=(), and getGamepads() then throws a
  // SecurityError. A forbidden or failing read is no pad at all, so the keyboard, taps, and the rest of the page keep working.
  function connectedPads() {
    try { return readPads(devices.gamepads()); } catch { return null; }
  }
  function tick() {
    frame = 0;
    if (life.disposed) return;
    const state = connectedPads();
    poll(state, previous);
    previous = state ?? idlePad();
    if (state) watch();
  }
  function watch() { if (!frame && !life.disposed) frame = devices.request(tick); }
  life.listen(target, 'gamepadconnected', watch);
  life.add(() => { if (frame) devices.cancel(frame); frame = 0; });
  if (connectedPads()) watch();
  return { dispose: () => life.dispose() };
}
